import { FC, forwardRef, type ComponentProps, type CSSProperties, type Ref } from "react";
import { DropdownListComponentProps } from "../../interface";
import SelectDropDown from "@CubeComponents/Mui_Form/SelectDropDown";

type Option = Record<string, unknown>;

/** Synthetic payload this component emits: `{ value, itemData }`. `target` is declared only because
 *  VariableRateModal reads `e.target.itemData` — see the KNOWN BUG note on the shared prop type. */
type DropDownListChangeArgs = {
  value?: unknown;
  itemData?: Record<string, unknown>;
  target?: { itemData?: Record<string, unknown> };
};

// `DropdownListComponentProps` omits the Syncfusion-era handlers and flags below; the rest of
// the props are forwarded to `SelectDropDown`.
interface DropDownListExtraProps {
  // Method shorthand makes these parameters bivariant, which is what lets VariableRateModal's
  // `{ value; target: { itemData } }` and CurrencySetting's `CurrencyChangeEvent` both fit; every
  // member is optional so each call site's shape is assignable to this one. See the matching note
  // on `DropdownListComponentProps.onChange`.
  onChange?(args: DropDownListChangeArgs): void;
  change?(args: DropDownListChangeArgs): void;
  disabled?: boolean;
  enabled?: boolean;
  allowFlexGrow?: boolean;
  /** Forwarded to `SelectDropDown` through `...rest`. */
  allowFiltering?: boolean;
}

const DropDownListComponent: FC<DropdownListComponentProps & DropDownListExtraProps> = forwardRef(
  (props: DropdownListComponentProps & DropDownListExtraProps, ref: Ref<unknown>) => {
    const {
      dataSource = [],
      fields = { text: "text", value: "value" },
      value,
      onChange,
      setValue,
      change,
      placeholder,
      disabled,
      enabled,
      width,
      allowFlexGrow,
      ...rest
    } = props;

    // `dataSource` is declared `unknown[]` on the shared props; every caller passes option
    // objects. Erased assertion, matching how the sibling pickers narrow it.
    const options: Option[] = Array.isArray(dataSource) ? (dataSource as Option[]) : [];
    const textField = fields?.text || "text";
    const valueField = fields?.value || "value";

    // enabled (Syncfusion) takes priority over disabled if both provided
    const isDisabled = enabled !== undefined ? !enabled : (disabled ?? false);

    // Resolve string value → option object
    const resolvedValue: Option | null = (() => {
      if (value == null) return null;
      if (typeof value === "object") return value as Option;
      return options.find((o) => o[valueField] === value) ?? null;
    })();

    const handleChange = (option: Option | Option[] | null) => {
      if (!option || Array.isArray(option)) return;
      const syntheticEvent = { value: option[valueField], itemData: option };
      if (onChange) onChange(syntheticEvent);
      if (setValue) setValue(syntheticEvent);
      if (change) change(syntheticEvent);
    };

    const containerStyle: CSSProperties = {
      width: width || "100%",
      display: "flex",
      flexDirection: "column",
      ...(allowFlexGrow ? { flexGrow: 1 } : {}),
    };

    return (
      <div style={containerStyle}>
        <SelectDropDown
          // @ts-expect-error -- a generically-created ref is forwarded to a MUI slot typed over a narrower element. Same element-type mismatch as `ToggleSwitchComponent` / `SelectDropDown`.
          ref={ref as ComponentProps<typeof SelectDropDown>["ref"]}
          options={options}
          value={resolvedValue}
          onChange={handleChange}
          placeholder={placeholder}
          disabled={isDisabled}
          fields={{ text: textField, value: valueField }}
          {...rest}
        />
      </div>
    );
  },
);

DropDownListComponent.displayName = "DropDownListComponent";
export default DropDownListComponent;
